import { useEffect, useState } from "react";

import router, { EVENT_CHANGE_ROUTE_COMPONENT } from "@/__main__/router.mjs";
import { appRoutes } from "@/routes/routes.mjs";
import appRouteRefs from "@/util/app-route.refs.mjs";
import { findGameSymbol } from "@/util/game-route.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

function findAppRoute(pathname: string) {
  return appRoutes.find(({ path }) => {
    if (!path) return false;
    if (pathname === path) return true;
    if (path instanceof RegExp && typeof pathname === "string")
      return path.test(pathname);
    return false;
  });
}

// non-hook version
export function shouldShowAppNavigation(inputPath?: string) {
  const pathname =
    inputPath ?? router.route?.currentPath ?? router.lastPathname;
  const gameSymbol = findGameSymbol(pathname);
  const appRoute = findAppRoute(pathname);
  const forceShowNavigation = router.RouteComponent?.showAppNavigation === true;
  return (
    !isRouteOverlay(pathname) &&
    (gameSymbol || appRoute || forceShowNavigation) &&
    router.RouteComponent?.showAppNavigation !== false
  );
}

/**
 * This centralizes where the logic is for showing the app navigation. For
 * some marketing routes we don't want to show app nav, for in-app or game
 * routes, we should show navigation.
 */
export function useShouldShowAppNavigation() {
  const [value, setValue] = useState(shouldShowAppNavigation());
  useEffect(() => {
    const listener = () => {
      setValue(shouldShowAppNavigation());
    };
    router.events.on(EVENT_CHANGE_ROUTE_COMPONENT, listener);
    return () => {
      router.events.off(EVENT_CHANGE_ROUTE_COMPONENT, listener);
    };
  }, []);
  return value;
}

/**
 * This centralizes where the logic is for showing the page header.
 */
export function useShouldShowPageHeader() {
  const shouldShowAppNavigation = useShouldShowAppNavigation();
  const { pageHeaderOverride } = useSnapshot(appRouteRefs);
  if (pageHeaderOverride !== null) return pageHeaderOverride;
  return shouldShowAppNavigation;
}
